import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Checkbox,
  List,
  Stack,
  Typography,
  Tooltip,
  IconButton
} from '@mui/material';
import { useFormik } from 'formik';
import _ from 'lodash';
import React from 'react';
import { DebounceInput } from 'react-debounce-input';
import { useMutation } from 'react-fetching-library';
import * as Yup from 'yup';

import { AutocompleteImpl } from '../../../../../components/AutoCompleteImpl';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { SmallFormDateField } from '../../../../../components/small-fields/SmallFormDateField';
import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { formatDate } from '../../../../../services/functions';
import {
  getPromotionsAction,
  submitPromotionAction
} from '../../../../../services/request';
import { MultiFriendsTable } from './MultiFriendsTable';
import { SelectingTable } from './SelectingTable';
import { useSelector } from 'react-redux';
import ClearIcon from '@mui/icons-material/Clear';

export const PromotionForm = props => {
  const {
    data,
    submitCallback,
    handleClose,
    treeObjects,
    allPeriods,
    allPackages,
    allCalculationTypes
  } = props;
  const treeRef = React.useRef();
  const { module, season } = useSelector(state => state.app);
  const { loading, mutate } = useMutation(submitPromotionAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required('To pole nie może być puste'),
    description: Yup.string(),
    type: Yup.string().nullable(),
    discount_code: Yup.string(),
    gross_value: Yup.number().required('To pole nie może być puste'),
    discount_code_from: Yup.date().nullable(),
    discount_code_to: Yup.date().nullable(),
    zs_discount_code_from: Yup.date().nullable(),
    zs_discount_code_to: Yup.date().nullable(),
    is_seasonal_combined: Yup.boolean(),
    is_enabled: Yup.boolean(),
    is_big_family_card: Yup.boolean(),
    is_connected_with_big_family_card: Yup.boolean(),
    can_connect_with_other_promotions: Yup.boolean(),
    calculation_type: Yup.string().required('To pole nie może być puste'),
    date_from: Yup.date().nullable(),
    date_to: Yup.date().nullable(),
    seasonal_promotion: Yup.boolean(),
    quick_payment_promotion: Yup.boolean(),
    show_discount_code_in_cms: Yup.boolean()
  });

  const initialValues = {
    name: data ? data.name || '' : '',
    description: data ? data.description || '' : '',
    discount_code: data ? data.discount_code || '' : '',
    gross_value: data ? data.gross_value || '' : '',
    discount_code_from: data ? data.discount_code_from || null : null,
    discount_code_to: data ? data.discount_code_to || null : null,
    zs_discount_code_from: data ? data.zs_discount_code_from || null : null,
    zs_discount_code_to: data ? data.zs_discount_code_to || null : null,
    is_enabled: data ? data.is_enabled || false : false,
    is_big_family_card: data ? data.is_big_family_card || false : false,
    is_connected_with_big_family_card: data
      ? data.is_connected_with_big_family_card || false
      : false,
    for_friend: data ? data.for_friend || false : false,
    for_siblings: data ? data.for_siblings || false : false,
    for_team: data ? data.for_team || false : false,
    sequence: data ? data.sequence || 1 : 1,
    can_connect_with_other_promotions: data
      ? data.can_connect_with_other_promotions || false
      : false,
    gross_values_per_number_of_friends: data
      ? data.gross_values_per_number_of_friends
      : [],
    promotions_excluded_from_connecting_with: data
      ? data.promotions_excluded_from_connecting_with_object
      : [],
    is_main_promotion: data ? data.is_main_promotion : false,
    main_promotion_association: data ? data.main_promotion_association : '',
    periods: data ? data.periods : [],
    packages: data ? data.packages : [],
    calculation_type: data ? data.calculation_type : 'fixed',
    date_from: data ? data.date_from || null : null,
    date_to: data ? data.date_to || null : null,
    seasonal_promotion: data ? data.seasonal_promotion || false : false,
    quick_payment_promotion: data ? data.quick_payment_promotion || false : false,
    is_seasonal_combined: data ? data.is_seasonal_combined || false : false,
    show_discount_code_in_cms: data ? data.show_discount_code_in_cms || false : false
  };

  const handleSubmit = async values => {
    values.season = season;
    if (treeRef.current) {
      values.tree = treeRef.current.getTree();
    }
    values.promotions_excluded_from_connecting_with = _.map(
      values.promotions_excluded_from_connecting_with,
      o => o.value
    );
    if (_.isObject(values.main_promotion_association)) {
      values.main_promotion_association =
        values.main_promotion_association.value;
    }
    const { error, payload } = await mutate(
      values,
      data ? 'PUT' : 'POST',
      data ? data.id : undefined
    );
    if (!error) {
      submitCallback(
        payload,
        data ? 'edit' : 'created',
        'success',
        data ? 'Zaktualizowano promocję' : 'Dodano nową promocję',
        ''
      );
      handleClose();
    } else {
      setSeverity('error');
      setTitle(
        data
          ? 'Nie udało się zaktualizować imprezy'
          : 'Nie udało się dodać nowej imprezy'
      );
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(
        data,
        'delete',
        'success',
        `Usunięto promocję ${data.name}`,
        ''
      );
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć promocji ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  React.useEffect(() => {
    if (formik.values.discount_code) {
      formik.setFieldValue('date_from', null);
      formik.setFieldValue('date_to', null);
    } else {
      formik.setFieldValue('discount_code_from', null);
      formik.setFieldValue('discount_code_to', null);
    }

  }, [formik.values.discount_code]);

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="row">
          <Box
            display="flex"
            flexDirection="column"
            mt={1}
            p={1}
            ml="5%"
            mr="5%"
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Nazwa
              </Typography>
              <DebounceInput
                sx={{ flex: '1 1 90%' }}
                name="name"
                id="name"
                type="text"
                placeholder="nazwa promocji"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                debounceTimeout={300}
                element={SmallFormTextField}
                autoFocus
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Opis
              </Typography>
              <DebounceInput
                sx={{ flex: '1 1 90%' }}
                name="description"
                id="description"
                type="text"
                placeholder="opis promocji"
                value={formik.values.description}
                onChange={formik.handleChange}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
                debounceTimeout={300}
                element={SmallFormTextField}
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                {formik.values.calculation_type == 'align_to_price_per_participant' ? 'Wyrównaj do' : 'Wartość'}
              </Typography>
              <DebounceInput
                sx={{ flex: '1 1 90%' }}
                name="gross_value"
                id="gross_value"
                type="text"
                placeholder={formik.values.calculation_type == 'align_to_price_per_participant' ? 'wyrównaj do' : 'wartość promocji'}
                value={formik.values.gross_value}
                onChange={formik.handleChange}
                error={
                  formik.touched.gross_value &&
                  Boolean(formik.errors.gross_value)
                }
                helperText={
                  formik.touched.gross_value && formik.errors.gross_value
                }
                debounceTimeout={300}
                element={SmallFormTextField}
              />
            </Stack>
            {['zielone_szkoly'].includes(module) && <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Rodzaj wyliczenia
              </Typography>
              <SmallFormTextField
                sx={{ flex: '1 1 90%' }}
                select
                id="calculation_type"
                name="calculation_type"
                options={allCalculationTypes}
                value={formik.values.calculation_type}
                onChange={formik.handleChange}
              />
            </Stack>}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Kod promocyjny
              </Typography>
              <DebounceInput
                sx={{ flex: '1 1 90%' }}
                name="discount_code"
                id="discount_code"
                type="text"
                placeholder="kod promocyjny"
                value={formik.values.discount_code}
                onChange={formik.handleChange}
                error={
                  formik.touched.discount_code &&
                  Boolean(formik.errors.discount_code)
                }
                helperText={
                  formik.touched.discount_code && formik.errors.discount_code
                }
                debounceTimeout={300}
                element={SmallFormTextField}
              />
            </Stack>
            {!formik.values.discount_code && ['obozy', 'wyjazdy_rodzinne'].includes(module) && <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Data promocji od
              </Typography>
              <SmallFormDateField
                sx={{ flex: '1 1 90%' }}
                name="date_from"
                id="date_from"
                type="text"
                placeholder="date_from"
                value={formik.values.date_from}
                onChange={val => formik.setFieldValue('date_from', formatDate(val))}
                error={
                  formik.touched.date_from &&
                  Boolean(formik.errors.date_from)
                }
                helperText={
                  formik.touched.date_from &&
                  formik.errors.date_from
                }
              />
              <Tooltip title="Wyczyść datę">
                <IconButton
                  onClick={() => formik.setFieldValue('date_from', null)}
                  size="small"
                  sx={{ color: '#888', '&:hover': { color: '#000' } }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>}
            {!formik.values.discount_code && ['obozy', 'wyjazdy_rodzinne'].includes(module) && <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Data promocji do
              </Typography>
              <SmallFormDateField
                sx={{ flex: '1 1 90%' }}
                name="date_to"
                id="date_to"
                type="text"
                placeholder="date_to"
                value={formik.values.date_to}
                onChange={val => formik.setFieldValue('date_to', formatDate(val))}
                error={
                  formik.touched.date_to &&
                  Boolean(formik.errors.date_to)
                }
                helperText={
                  formik.touched.date_to &&
                  formik.errors.date_to
                }
              />
              <Tooltip title="Wyczyść datę">
                <IconButton
                  onClick={() => formik.setFieldValue('date_to', null)}
                  size="small"
                  sx={{ color: '#888', '&:hover': { color: '#000' } }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>}
            {formik.values.discount_code && <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Data kodu promocyjnego od
              </Typography>
              <SmallFormDateField
                sx={{ flex: '1 1 90%' }}
                name="discount_code_from"
                id="discount_code_from"
                type="text"
                placeholder="discount_code_from"
                value={formik.values.discount_code_from}
                onChange={val => formik.setFieldValue('discount_code_from', formatDate(val))}
                error={
                  formik.touched.discount_code_from &&
                  Boolean(formik.errors.discount_code_from)
                }
                helperText={
                  formik.touched.discount_code_from &&
                  formik.errors.discount_code_from
                }
              />
              <Tooltip title="Wyczyść datę">
                <IconButton
                  onClick={() => formik.setFieldValue('discount_code_from', null)}
                  size="small"
                  sx={{ color: '#888', '&:hover': { color: '#000' } }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>}
            {formik.values.discount_code && <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Data kodu promocyjnego do
              </Typography>
              <SmallFormDateField
                sx={{ flex: '1 1 90%' }}
                name="discount_code_to"
                id="discount_code_to"
                type="text"
                placeholder="discount_code_to"
                value={formik.values.discount_code_to}
                onChange={val => formik.setFieldValue('discount_code_to', formatDate(val))}
                error={
                  formik.touched.discount_code_to &&
                  Boolean(formik.errors.discount_code_to)
                }
                helperText={
                  formik.touched.discount_code_to &&
                  formik.errors.discount_code_to
                }
              />
              <Tooltip title="Wyczyść datę">
                <IconButton
                  onClick={() => formik.setFieldValue('discount_code_to', null)}
                  size="small"
                  sx={{ color: '#888', '&:hover': { color: '#000' } }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>}
            {formik.values.discount_code && module === 'zielone_szkoly' && <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Data kodu promocyjnego od - kalendarz ZS
              </Typography>
              <SmallFormDateField
                sx={{ flex: '1 1 90%' }}
                name="zs_discount_code_from"
                id="zs_discount_code_from"
                type="text"
                placeholder="zs_discount_code_from"
                value={formik.values.zs_discount_code_from}
                onChange={val => formik.setFieldValue('zs_discount_code_from', formatDate(val))}
                error={
                  formik.touched.zs_discount_code_from &&
                  Boolean(formik.errors.zs_discount_code_from)
                }
                helperText={
                  formik.touched.zs_discount_code_from &&
                  formik.errors.zs_discount_code_from
                }
              />
              <Tooltip title="Wyczyść datę">
                <IconButton
                  onClick={() => formik.setFieldValue('zs_discount_code_from', null)}
                  size="small"
                  sx={{ color: '#888', '&:hover': { color: '#000' } }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>}
            {formik.values.discount_code && module === 'zielone_szkoly' && <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Data kodu promocyjnego do - kalendarz ZS
              </Typography>
              <SmallFormDateField
                sx={{ flex: '1 1 90%' }}
                name="zs_discount_code_to"
                id="zs_discount_code_to"
                type="text"
                placeholder="zs_discount_code_to"
                value={formik.values.zs_discount_code_to}
                onChange={val => formik.setFieldValue('zs_discount_code_to', formatDate(val))}
                error={
                  formik.touched.zs_discount_code_to &&
                  Boolean(formik.errors.zs_discount_code_to)
                }
                helperText={
                  formik.touched.zs_discount_code_to &&
                  formik.errors.zs_discount_code_to
                }
              />
              <Tooltip title="Wyczyść datę">
                <IconButton
                  onClick={() => formik.setFieldValue('zs_discount_code_to', null)}
                  size="small"
                  sx={{ color: '#888', '&:hover': { color: '#000' } }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>}
            {formik.values.discount_code && module === 'obozy' && <Stack
              direction="row"
              alignItems="center"
              justifyContent="right"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Pokazuj kod rabatowy w CMS
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="show_discount_code_in_cms"
                name="show_discount_code_in_cms"
                checked={formik.values.show_discount_code_in_cms}
                onChange={formik.handleChange}
              />
            </Stack>
            }
            {module === 'obozy' && <Stack
              direction="row"
              alignItems="center"
              justifyContent="right"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Czy łączy się z promocją sezonową
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="is_seasonal_combined"
                name="is_seasonal_combined"
                checked={formik.values.is_seasonal_combined}
                onChange={formik.handleChange}
              />
            </Stack>
            }
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="right"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Jest aktywna
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="is_enabled"
                name="is_enabled"
                checked={formik.values.is_enabled}
                onChange={formik.handleChange}
              />
            </Stack>
            { module == 'obozy' && <Stack
              direction="row"
              alignItems="center"
              justifyContent="right"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Promocja sezonowa
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="seasonal_promotion"
                name="seasonal_promotion"
                checked={formik.values.seasonal_promotion}
                onChange={formik.handleChange}
              />
            </Stack>}
            { ['obozy', 'wyjazdy_rodzinne'].includes(module) && <Stack
              direction="row"
              alignItems="center"
              justifyContent="right"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Promocja szybkiej płatności
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="quick_payment_promotion"
                name="quick_payment_promotion"
                checked={formik.values.quick_payment_promotion}
                onChange={formik.handleChange}
              />
            </Stack>}
            {['obozy', 'wyjazdy_rodzinne'].includes(module) && <Stack
              direction="row"
              alignItems="center"
              justifyContent="right"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Promocja dla posiadaczy KDR
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="is_big_family_card"
                name="is_big_family_card"
                checked={formik.values.is_big_family_card}
                onChange={formik.handleChange}
              />
            </Stack>}
            {['obozy', 'wyjazdy_rodzinne'].includes(module) && <Stack
              direction="row"
              alignItems="left"
              justifyContent="left"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Połączona z kartą dużej rodziny
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="is_connected_with_big_family_card"
                name="is_connected_with_big_family_card"
                checked={formik.values.is_connected_with_big_family_card}
                onChange={formik.handleChange}
              />
            </Stack>}
            {['obozy', 'wyjazdy_rodzinne'].includes(module) && <Stack
              direction="row"
              alignItems="left"
              justifyContent="left"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Dla przyjaciela
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="for_friend"
                name="for_friend"
                checked={formik.values.for_friend}
                onChange={formik.handleChange}
              />
            </Stack>}
            {['obozy', 'wyjazdy_rodzinne'].includes(module) && <Stack
              direction="row"
              alignItems="left"
              justifyContent="left"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Dla rodzeństwa
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="for_siblings"
                name="for_siblings"
                checked={formik.values.for_siblings}
                onChange={formik.handleChange}
              />
            </Stack>}
            {['obozy', 'wyjazdy_rodzinne'].includes(module) && <Stack
              direction="row"
              alignItems="left"
              justifyContent="left"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Dla ekipy
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="for_team"
                name="for_team"
                checked={formik.values.for_team}
                onChange={formik.handleChange}
              />
            </Stack>}
            {['wyjazdy_rodzinne', 'zielone_szkoly'].includes(module) && <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Terminy
              </Typography>
              <SmallFormTextField
                sx={{ flex: '1 1 90%' }}
                select
                multiple
                id="periods"
                name="periods"
                options={allPeriods}
                value={formik.values.periods}
                onChange={formik.handleChange}
              />
            </Stack>}
            {['zielone_szkoly'].includes(module) && <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Pakiety
              </Typography>
              <SmallFormTextField
                sx={{ flex: '1 1 90%' }}
                select
                multiple
                id="packages"
                name="packages"
                options={allPackages}
                value={formik.values.packages}
                onChange={formik.handleChange}
              />
            </Stack>}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Kolejność
              </Typography>
              <DebounceInput
                sx={{ flex: '1 1 90%' }}
                name="sequence"
                id="sequence"
                type="text"
                placeholder="kolejność"
                value={formik.values.sequence}
                onChange={formik.handleChange}
                error={
                  formik.touched.sequence && Boolean(formik.errors.sequence)
                }
                helperText={formik.touched.sequence && formik.errors.sequence}
                debounceTimeout={300}
                element={SmallFormTextField}
              />
            </Stack>
            {['obozy', 'wyjazdy_rodzinne'].includes(module) && <Stack
              direction="row"
              alignItems="center"
              justifyContent="right"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Jest główną promocją
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="is_main_promotion"
                name="is_main_promotion"
                checked={formik.values.is_main_promotion}
                onChange={e => {
                  formik.handleChange(e);
                  formik.setFieldValue('main_promotion_association', null);
                }}
              />
            </Stack>}
            {!formik.values.is_main_promotion && ['obozy', 'wyjazdy_rodzinne'].includes(module) && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="right"
                spacing={1}
                mb={2}
              >
                <Typography variant="subtitle2" flex="1 1 25%">
                  Główna promocja
                </Typography>
                <AutocompleteImpl
                  sx={{ flex: '1 1 75%' }}
                  // defaultValue={formik.values.main_promotion_association}
                  displayName={data ? data.main_promotion_display : ''}
                  action={getPromotionsAction}
                  fieldName="main_promotion_association"
                  formik={formik}
                />
              </Stack>
            )}
            {['obozy', 'wyjazdy_rodzinne'].includes(module) && <Stack
              direction="row"
              alignItems="left"
              justifyContent="left"
              spacing={1}
              mb={2}
            >
              <Typography variant="subtitle2" flex="1 1 25%">
                Łączy się z innymi promocjami
              </Typography>
              <Checkbox
                sx={{ flex: '1 1 90%' }}
                id="can_connect_with_other_promotions"
                name="can_connect_with_other_promotions"
                checked={formik.values.can_connect_with_other_promotions}
                onChange={formik.handleChange}
              />
            </Stack>}
            {['obozy', 'wyjazdy_rodzinne'].includes(module) && <Stack
              direction="row"
              alignItems="center"
              justifyContent="right"
              spacing={1}
            >
              <Typography variant="subtitle2" flex="1 1 30%">
                Połączone promocje
              </Typography>
              <AutocompleteImpl
                multiple={true}
                sx={{ flex: '1 1 70%' }}
                defaultValue={
                  formik.values.promotions_excluded_from_connecting_with
                }
                displayName={
                  formik.values.promotions_excluded_from_connecting_with
                }
                action={getPromotionsAction}
                fieldName="promotions_excluded_from_connecting_with"
                formik={formik}
              />
            </Stack>}
            {(formik.values.for_friend || formik.values.for_team) ? (
              <MultiFriendsTable formik={formik} />
            ) : null}
          </Box>
          {!['wyjazdy_rodzinne', 'zielone_szkoly'].includes(module) && <Box>
            <List disablePadding>
              {!_.isEmpty(treeObjects) ? (
                <SelectingTable
                  data={treeObjects}
                  ref={treeRef}
                  initSelected={data ? data.init_selected_tree : []}
                />
              ) : null}
            </List>
          </Box>}
        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          <AddIcon />
          Zapisz
        </Button>
        {data ? (
          <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
          >
            <DeleteIcon />
            Usuń
          </Button>
        ) : null}
      </form>
      <ConfirmDialog
        title="Usunąć Promocję?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
        disabled={loading}
      >
        Czy na pewno chcesz usunąć tą promocję?
      </ConfirmDialog>
    </>
  );
};
